import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Resizer, { RESIZER_EXPAND_LAST, RESIZER_ORIENTATION_HORIZONTAL } from '../shared/resizer';
import Viewport from '../shared/viewport';
import Environment from '../shared/environment';
import './sequencer.css';
import { loadWorldLocal, loadWorldRemote, loadAssetLibraryLocal, loadAssetLibraryRemote } from '../shared/loaders';
import { copyText } from '../shared/browser-utils';

export default function Sequencer() {
  let { project } = useParams();
  const [sceneIndex, setSceneIndex] = useState(0);
  const [world, setWorld] = useState();
  const [assetLibrary, setAssetLibrary] = useState();
  const [version, setVersion] = useState(0);
  const [cameraVersion, setCameraVersion] = useState(0);
  const [tmpCameraPosition, setTmpCameraPosition] = useState([0, 0, 0]);
  const [tmpCameraRotation, setTmpCameraRotation] = useState([0, 0, 0]);
  let sequence;

  // Load the world and asset library
  useEffect(() => {
    async function load() {
      const remote = document.location.search.includes('remote');
      if (remote) {
        const world = await loadWorldRemote(sequence.db);
        setWorld(world);
        setAssetLibrary(await loadAssetLibraryRemote(world.assetLibrary));
      } else {
        const world = await loadWorldLocal(sequence.db);
        setWorld(world);
        setAssetLibrary(await loadAssetLibraryLocal(world.assetLibrary));
      }
      setVersion((version) => version + 1);
      const firstCameraIndex = sequence.scenes.findIndex((scene) => scene.cameraPosition[0] !== 0);
      setSceneIndex(firstCameraIndex);
    }
    load();
  }, [sequence]);

  useEffect(() => {
    if (!world) return;
    let scene = sequence.scenes[sceneIndex];
    for (const layer of world.layers) {
      layer.visible = scene.layers.includes(layer.name);
    }
    setVersion((version) => version + 1);
  }, [sequence, sceneIndex, world]);

  const sequenceModule = require(`../player/${project}-sequence`);
  if (!sequenceModule) return <div>not found.</div>;
  sequence = sequenceModule.default;

  let currentScene = sequence.scenes[sceneIndex];
  let environment = new Environment();

  const prevScene = () => {
    if (sceneIndex <= 0) return;
    setSceneIndex(sceneIndex - 1);
  };

  const nextScene = () => {
    if (sceneIndex >= sequence.scenes.length - 1) return;
    setSceneIndex(sceneIndex + 1);
  };

  const resetCameraPosition = () => {
    setCameraVersion((version) => version + 1);
  };

  const onChangeCamera = (position, rotation) => {
    console.log(position, rotation);
    setTmpCameraPosition([position.x, position.y, position.z]);
    setTmpCameraRotation([rotation.x, rotation.y, rotation.z]);
  };

  const setCameraPosition = () => {
    sequence.scenes[sceneIndex].cameraPosition = tmpCameraPosition.slice();
    sequence.scenes[sceneIndex].cameraRotation = tmpCameraRotation.slice();
    setVersion((version) => version + 1);
    console.log('set');
  };

  const exportSequence = () => {
    let code = '';
    const sceneSlugs = sequence.scenes.map((s) => s.slug);
    for (const scene of sequence.scenes) {
      code += `const ${scene.slug} = ${JSON.stringify(scene, null, 2)};\n\n`;
    }
    code += `const sequence = {\n`;
    code += `  name: '${sequence.name}',\n`;
    code += `  db: '${sequence.db}',\n`;
    code += `  scenes: [${sceneSlugs.join(', ')}],\n`;
    code += `  layerMap: ${JSON.stringify(sequence.layerMap, null, 2)},\n`;
    code += `};\n`;
    code += `\n`;
    code += `export default sequence;\n`;

    copyText(code);
  };

  if (!currentScene) return <div>Loading...</div>;

  return (
    <div className="sequencer">
      <div className="sequencer__header">Sequencer — {sequence.name}</div>
      <Resizer orientation={RESIZER_ORIENTATION_HORIZONTAL} size={300} expand={RESIZER_EXPAND_LAST}>
        <div className="preview">
          <Viewport
            world={world}
            environment={environment}
            scene={currentScene}
            version={version}
            assetLibrary={assetLibrary}
            cameraPosition={currentScene.cameraPosition}
            cameraRotation={currentScene.cameraRotation}
            cameraVersion={cameraVersion}
            disableCameraAnimation={true}
            controlsEnabled={true}
            onChangeCamera={onChangeCamera}
          />
        </div>
        <div className="props">
          <div className="panel__tabs">
            <button className="panel__tab" onClick={prevScene}>
              &lt;
            </button>
            <button className="panel__tab" onClick={nextScene}>
              &gt;
            </button>
            <div className="panel__tab active">
              #{sceneIndex} {currentScene.slug}
            </div>
          </div>
          <div className="panel__content">
            <div className="props__row">
              <div className="props__label">Camera</div>
            </div>
            <div className="props__row">
              <label className="props__label">Position</label>
              <span className="props__coordName">X</span>
              <input className="props__coord" type="text" readOnly value={currentScene.cameraPosition[0]} />
              <span className="props__coordName">Y</span>
              <input className="props__coord" type="text" readOnly value={currentScene.cameraPosition[1]} />
              <span className="props__coordName">Z</span>
              <input className="props__coord" type="text" readOnly value={currentScene.cameraPosition[2]} />
            </div>
            <div className="props__row">
              <label className="props__label">Rotation</label>
              <span className="props__coordName">X</span>
              <input className="props__coord" type="text" readOnly value={currentScene.cameraRotation[0]} />
              <span className="props__coordName">Y</span>
              <input className="props__coord" type="text" readOnly value={currentScene.cameraRotation[1]} />
              <span className="props__coordName">Z</span>
              <input className="props__coord" type="text" readOnly value={currentScene.cameraRotation[2]} />
            </div>
            <div className="props__row">
              <button onClick={setCameraPosition}>Set</button>
              <button onClick={resetCameraPosition}>Reset</button>
            </div>

            <div className="props__row">
              <div className="props__action">
                <button onClick={exportSequence}>Export</button>
              </div>
            </div>
          </div>
        </div>
      </Resizer>
    </div>
  );
}
